import React from 'react';
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck, Zap, AlertCircle } from "lucide-react";
import { getEnabledExchanges, getSymbols } from '../../dca/ExchangeUIConfigs';

interface SignalBasicSettingsProps {
  botData: any;
  updateBotData: (key: string, value: any) => void;
  updateSettings: (key: string, value: any) => void;
  updateMultiple: (updates: Record<string, any>) => void;
  isRunning: boolean;
  enabledExchanges: Record<string, boolean>;
  currency: string;
  effectiveMargin: number;
  positionSize: number;
}

export const SignalBasicSettings: React.FC<SignalBasicSettingsProps> = ({
  botData,
  updateBotData,
  updateSettings,
  updateMultiple,
  isRunning,
  enabledExchanges,
  currency,
  effectiveMargin,
  positionSize
}) => {
  const {
    name, symbol, exchange, leverage, entry_amount,
    is_live, use_testnet, interval, principal_amount, entry_type, strategy_type, settings,
    source
  } = botData;

  return (
    <div className="space-y-6">
      {/* DCA 스타일 콤팩트 기본 설정 그리드 */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 text-xs">
        <div className="space-y-1">
          <Label className="text-[9px] font-bold text-muted-foreground uppercase px-1">봇 이름 (Bot Name)</Label>
          <Input
            value={name || ''}
            onChange={(e) => updateBotData('name', e.target.value)}
            placeholder="봇 이름을 입력하세요"
            className="h-8 text-xs font-black border-border rounded-lg bg-background focus:ring-1 focus:ring-primary shadow-sm"
          />
        </div>

        <div className="space-y-1">
          <Label className="text-[9px] font-bold text-muted-foreground uppercase px-1">매매 전략 (Strategy)</Label>
          <Select disabled={isRunning} value={strategy_type || 'hull_macd'} onValueChange={(v) => {
            updateBotData('strategy_type', v);
          }}>
            <SelectTrigger className={`h-8 text-xs font-black border-border rounded-lg focus:ring-1 focus:ring-primary shadow-sm ${isRunning ? 'bg-muted/50 opacity-80' : 'bg-background'}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent side="bottom" className="bg-card border-border max-h-[50vh] overflow-y-auto">
              <SelectItem value="auto_signal">[auto_signal] CenIchi 4Ema Pullback (Auto1)</SelectItem>
              <SelectItem value="zerolag_macd">[zerolag_macd] ZeroLag MACD (DEMA)</SelectItem>
              <SelectItem value="hybrid_pullback">[hybrid_pullback] Hybrid Pullback</SelectItem>
              <SelectItem value="macd_ema">[macd_ema] MACD + EMA 20</SelectItem>
              <SelectItem value="stochastic_ema">[stochastic_ema] Stochastic + EMA 10 (1h/5m 조합)</SelectItem>
              <SelectItem value="perfect_alignment_pullback">[perfect_alignment_pullback] Perfect Alignment Pullback (EMA 60/89/120/240)</SelectItem>
              <SelectItem value="channel_trend">[channel_trend] Channel Trend (LR)</SelectItem>
              <SelectItem value="channel_trend2">[channel_trend2] Channel Trend V2</SelectItem>
              <SelectItem value="kewma">[kewma] KEWMA (Kalman EMA)</SelectItem>
              <SelectItem value="ict">[ict] ICT Strategy :: FVG + OB + MSS 기관 딜리버링</SelectItem>
              <SelectItem value="super_hybrid">[super_hybrid] Macro Score + KEWMA + SuperTrend Cluster</SelectItem>
              <SelectItem value="score_based_signal">[score_based_signal] Score Based Signal (다중지표 스코어)</SelectItem>
              <SelectItem value="tsp_stoch_realtime">[tsp_stoch_realtime] TSP Stochastic Realtime (Tsp + Stoch)</SelectItem>
              <SelectItem value="strider_dca">[strider_dca] Strider DCA (급락 분할 매수)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label className="text-[9px] font-bold text-muted-foreground uppercase px-1">거래소 (Exchange)</Label>
          <Select disabled={isRunning} value={exchange} onValueChange={(v) => updateBotData('exchange', v)}>
            <SelectTrigger className={`h-8 text-xs font-black border-border rounded-lg focus:ring-1 focus:ring-primary shadow-sm ${isRunning ? 'bg-muted/50 opacity-80' : 'bg-background'}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              {getEnabledExchanges(enabledExchanges).map(ex => (
                <SelectItem key={ex.value} value={ex.value}>{ex.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label className="text-[9px] font-bold text-muted-foreground uppercase px-1">거래 모드 (Trading Mode)</Label>
          <Select disabled={isRunning} value={is_live ? 'live' : 'paper'} onValueChange={(v) => updateMultiple({ is_live: v === 'live' })}>
            <SelectTrigger className="h-8 text-xs font-black border-border bg-background rounded-lg focus:ring-1 focus:ring-primary shadow-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="paper">
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                  <span className="text-xs font-bold">시뮬레이션 (Paper)</span>
                </div>
              </SelectItem>
              <SelectItem value="live">
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                  <span className="text-xs font-bold">실매매 (Live)</span>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {is_live && (
          <div className="space-y-1">
            <Label className="text-[9px] font-bold text-muted-foreground uppercase px-1">접속 환경 (Network)</Label>
            <Select disabled={isRunning} value={use_testnet ? 'testnet' : 'mainnet'} onValueChange={(v) => updateMultiple({ use_testnet: v === 'testnet' })}>
              <SelectTrigger className="h-8 text-xs font-black border-border bg-background rounded-lg focus:ring-1 focus:ring-primary shadow-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="mainnet">
                  <div className="flex items-center gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5 text-success" />
                    <span className="text-xs font-bold">메인넷 (Mainnet)</span>
                  </div>
                </SelectItem>
                <SelectItem value="testnet">
                  <div className="flex items-center gap-1.5">
                    <Zap className="w-3.5 h-3.5 text-amber-500" />
                    <span className="text-xs font-bold">테스트넷 (Testnet)</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="space-y-1">
          <Label className="text-[9px] font-bold text-muted-foreground uppercase px-1">티커 심볼 (Symbol)</Label>
          <Select disabled={isRunning} value={symbol} onValueChange={(v) => updateBotData('symbol', v)}>
            <SelectTrigger className={`h-8 text-xs font-black border-border rounded-lg focus:ring-1 focus:ring-primary shadow-sm ${isRunning ? 'bg-muted/50 opacity-80' : 'bg-background'}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card border-border max-h-[300px] overflow-y-auto">
              {(() => {
                const allSymbols = getSymbols(exchange, use_testnet);
                const recommendedBases = [
                  'BTC', 'SOL', 'ETH', 'XRP', 'DOGE', 'ENA'
                ];

                const getBase = (val: string) => {
                  return val.replace('-USDT-SWAP', '').replace('USDT', '');
                };

                const recommended = allSymbols.filter(s => recommendedBases.includes(getBase(s.value)));
                const others = allSymbols.filter(s => !recommendedBases.includes(getBase(s.value)));

                return (
                  <>
                    <div className="px-2 py-1 text-[9px] font-black text-muted-foreground uppercase tracking-wider bg-muted/20">✨ 추천 우수 코인</div>
                    {recommended.map(sym => (
                      <SelectItem key={sym.value} value={sym.value}>{sym.label}</SelectItem>
                    ))}
                    {others.length > 0 && (
                      <>
                        <div className="h-px bg-border my-1" />
                        <div className="px-2 py-1 text-[9px] font-black text-muted-foreground uppercase tracking-wider bg-muted/15">기타 코인</div>
                        <div className="grid grid-cols-2 gap-1 p-1">
                          {others.map(sym => (
                            <SelectItem key={sym.value} value={sym.value}>{sym.label}</SelectItem>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                );
              })()}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label className="text-[9px] font-bold text-muted-foreground uppercase px-1">캔들 간격 (Interval)</Label>
          <Select value={interval} onValueChange={(v) => updateBotData('interval', v)}>
            <SelectTrigger className="h-8 text-xs font-black border-border bg-background rounded-lg focus:ring-1 focus:ring-primary shadow-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="5m">5m</SelectItem>
              <SelectItem value="15m">15m</SelectItem>
              <SelectItem value="30m">30m</SelectItem>
              <SelectItem value="1h">1h</SelectItem>
              <SelectItem value="4h">4h</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">사용 시드 (Budget)</Label>
          <div className="relative">
            <Input
              type="number"
              value={principal_amount}
              onChange={(e) => updateBotData('principal_amount', Number(e.target.value))}
              className="h-10 text-sm font-black border-border rounded-xl pr-16 bg-background"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 font-bold text-muted-foreground text-sm">USDT</span>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between px-1">
            <Label className="text-[10px] font-black text-muted-foreground uppercase">진입 금액 (Margin)</Label>
            <div className="flex bg-muted rounded-md p-0.5 h-6">
              <button
                type="button"
                onClick={() => updateBotData('entry_type', 'percent')}
                className={`px-2 h-full text-[9px] font-bold rounded ${entry_type === 'percent' ? 'bg-background shadow-xs text-foreground' : 'text-muted-foreground'}`}
              >
                %
              </button>
              <button
                type="button"
                onClick={() => updateBotData('entry_type', 'fixed')}
                className={`px-2 h-full text-[9px] font-bold rounded ${entry_type === 'fixed' ? 'bg-background shadow-xs text-foreground' : 'text-muted-foreground'}`}
              >
                $
              </button>
            </div>
          </div>
          <div className="relative">
            <Input
              type="number"
              value={entry_amount}
              onChange={(e) => updateBotData('entry_amount', Number(e.target.value))}
              className="h-10 text-sm font-black border-border rounded-xl pr-16 bg-background"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 font-bold text-muted-foreground text-sm">
              {entry_type === 'percent' ? '%' : 'USDT'}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
        <div className="space-y-2">
          <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">사용 레버리지 (x{leverage})</Label>
          <div className="pt-2">
            <Slider disabled={isRunning} value={[leverage]} max={50} min={1} step={1} onValueChange={(val) => updateBotData('leverage', val[0])} />
            <div className="flex justify-between text-[9px] text-muted-foreground font-bold px-1 mt-2 uppercase">
              <span>1X</span><span>25X</span><span>50X</span>
            </div>
          </div>
        </div>

        <div className="bg-muted/30 p-4 rounded-2xl border border-border/50">
          <div className="flex justify-between items-center mb-1">
            <span className="text-[10px] font-black text-muted-foreground uppercase tracking-tight">진입 규모 (Notional)</span>
            <span className="text-[10px] font-black text-primary uppercase">x{leverage} Leverage</span>
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-xl font-black">{positionSize.toLocaleString()}</span>
            <span className="text-xs font-bold text-muted-foreground uppercase">USDT</span>
          </div>
          <p className="text-[9px] text-muted-foreground font-medium mt-1">
            증거금 {effectiveMargin.toLocaleString()} USDT × {leverage}배
          </p>
        </div>
      </div>

      {/* 공통: 수수료율 */}
      <div className="grid grid-cols-2 gap-3 p-4 border border-border/50 rounded-2xl bg-card">
        <Label className="text-[10px] font-black text-muted-foreground uppercase col-span-full">거래 수수료율 (Fee Rate)</Label>
        <div className="space-y-1.5">
          <Label className="text-[10px] text-muted-foreground font-medium">시장가 (Market)</Label>
          <div className="flex items-center gap-1.5">
            <Input
              type="number"
              step="0.001"
              min="0"
              max="1"
              value={settings.fee_rate_market ?? 0.05}
              onChange={(e) => updateSettings('fee_rate_market', parseFloat(e.target.value) || 0)}
              className="h-8 text-xs font-bold text-right"
            />
            <span className="text-xs text-muted-foreground font-bold">%</span>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-[10px] text-muted-foreground font-medium">지정가 (Limit)</Label>
          <div className="flex items-center gap-1.5">
            <Input
              type="number"
              step="0.001"
              min="0"
              max="1"
              value={settings.fee_rate_limit ?? 0.02}
              onChange={(e) => updateSettings('fee_rate_limit', parseFloat(e.target.value) || 0)}
              className="h-8 text-xs font-bold text-right"
            />
            <span className="text-xs text-muted-foreground font-bold">%</span>
          </div>
        </div>
      </div>

      <div className="border-t border-border/50 pt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <div className="flex justify-between items-center px-1">
            <Label className="text-[10px] font-black text-muted-foreground uppercase">목표 익절 (Target TP %)</Label>
            <span className="text-[10px] font-black text-success bg-success/10 px-1.5 py-0.5 rounded">
              ROE {((settings.tp_pct ?? 1.5) * leverage).toFixed(1)}%
            </span>
          </div>
          <div className="relative">
            <Input
              type="number"
              step="any"
              value={settings.tp_pct ?? ''}
              placeholder="1.5"
              onChange={(e) => updateSettings('tp_pct', e.target.value === '' ? '' : Number(e.target.value))}
              className="h-10 text-sm font-black border-border rounded-xl pr-10 bg-background"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 font-bold text-success text-sm">%</span>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between items-center px-1">
            <Label className="text-[10px] font-black text-muted-foreground uppercase">최대 손절 (Stop Loss %)</Label>
            <span className="text-[10px] font-black text-danger bg-danger/10 px-1.5 py-0.5 rounded">
              ROE {((settings.sl_pct ?? 1.0) * leverage).toFixed(1)}%
            </span>
          </div>
          <div className="relative">
            <Input
              type="number"
              step="any"
              value={settings.sl_pct ?? ''}
              placeholder="1.0"
              onChange={(e) => updateSettings('sl_pct', e.target.value === '' ? '' : Number(e.target.value))}
              className="h-10 text-sm font-black border-border rounded-xl pr-10 bg-background"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 font-bold text-danger text-sm">%</span>
          </div>
        </div>
      </div>

      <div className="p-4 bg-primary/5 border border-primary/20 rounded-2xl flex items-center justify-between mb-4">
        <div className="space-y-0.5">
          <div className="flex items-center gap-1.5">
            <Zap className="w-4 h-4 text-primary" />
            <Label className="text-sm font-black text-primary-700">지정가 주문 사용 (Use Limit Order)</Label>
          </div>
          <p className="text-[10px] text-primary-600/80 font-bold">체크 시 진입 및 청산(부분 익절 포함) 시 시장가 대신 지정가(Limit) 주문을 제출합니다.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] font-black ${settings.use_limit_order ? 'text-primary' : 'text-muted-foreground'}`}>
            {settings.use_limit_order ? '사용 중' : '미사용'}
          </span>
          <Checkbox
            checked={settings.use_limit_order || false}
            onCheckedChange={(checked) => updateSettings('use_limit_order', checked)}
            className="w-5 h-5 border-primary/50 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
          />
        </div>
      </div>

      <div className="p-4 bg-amber-500/5 border border-amber-500/20 rounded-2xl flex items-center justify-between mb-4">
        <div className="space-y-0.5">
          <div className="flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-amber-500" />
            <Label className="text-sm font-black text-amber-700">24시간 자동 정지 (Safe Stop)</Label>
          </div>
          <p className="text-[10px] text-amber-600/80 font-bold">가동 후 24시간이 지나면 자동으로 정지되어 시장 상황을 수동으로 재점검하게 합니다.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] font-black ${settings.use_auto_stop ? 'text-amber-600' : 'text-muted-foreground'}`}>
            {settings.use_auto_stop ? '사용 중' : '미사용'}
          </span>
          <Checkbox
            checked={settings.use_auto_stop || false}
            onCheckedChange={(checked) => updateSettings('use_auto_stop', checked)}
            className="w-5 h-5 border-amber-500/50 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
          />
        </div>
      </div>

      {source === 'auto_scanner' && (
        <div className="p-4 bg-red-500/5 border border-red-500/20 rounded-2xl flex items-center justify-between">
          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5">
              <AlertCircle className="w-4 h-4 text-red-500" />
              <Label className="text-sm font-black text-red-700">진입 신호 무시 (Ignore Signal)</Label>
            </div>
            <p className="text-[10px] text-red-600/80 font-bold">체크 시, 스캐너나 외부 신호가 감지되더라도 진입하지 않고 신호를 무시합니다.</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-[10px] font-black ${settings.ignore_signal ? 'text-red-600' : 'text-muted-foreground'}`}>
              {settings.ignore_signal ? '무시 중' : '미사용'}
            </span>
            <Checkbox
              checked={settings.ignore_signal || false}
              onCheckedChange={(checked) => updateSettings('ignore_signal', checked)}
              className="w-5 h-5 border-red-500/50 data-[state=checked]:bg-red-500 data-[state=checked]:border-red-500"
            />
          </div>
        </div>
      )}
    </div>
  );
};
